import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Loader2,
  PauseCircle,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";
import { IconButton } from "@/components/atoms/IconButton";
import { cn } from "@/lib/cn";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { isSyncAltoSyncing, normalizeSyncAltoStatus, type SyncAltoStatus } from "@/lib/syncAlto";
import { crmGatewaySyncService, type ResyncEntityType } from "@/services/crmGatewaySyncService";
import { useSettingsStore } from "@/store/settingsStore";

/** Only Alto records surface a sync status today, so /crmgateway/resync gets ALTO by default. */
const DEFAULT_CRM_TYPE = "ALTO";

const PRESENTATION: Record<SyncAltoStatus, { label: string; className: string; Icon: LucideIcon }> = {
  synced: { label: "Synced", className: "text-emerald-700", Icon: CheckCircle2 },
  in_progress: { label: "Syncing", className: "text-blue-700", Icon: Loader2 },
  pending: { label: "Pending", className: "text-blue-700", Icon: Clock },
  paused: { label: "Paused", className: "text-amber-700", Icon: PauseCircle },
  checkpoint: { label: "Checkpoint", className: "text-amber-700", Icon: AlertCircle },
  failed: { label: "Sync failed", className: "text-rose-700", Icon: AlertTriangle },
  // No Alto status at all — e.g. a record from a non-Alto CRM. Distinct from a
  // FAILED sync: nothing is known to be wrong, so resync stays available.
  unknown: { label: "Sync pending", className: "text-orange-500", Icon: HelpCircle },
};

interface Props {
  /** Raw `crmSyncStatus` status from the list payload — e.g. "COMPLETED", "IN_PROGRESS". */
  crmSyncStatus?: string | null;
  /**
   * Which record kind to resync. Given this, the button calls the writeback
   * endpoint itself and the caller needs no handler; without it the button is
   * inert unless `onResync` is passed.
   */
  entityType?: ResyncEntityType;
  /** Atlas id of the record to write back. Required alongside `entityType`. */
  entityId?: string;
  /** Defaults to ALTO; pass the record's `sourceCrm` once other CRMs sync. */
  crmType?: string;
  /** Fired after a resync is queued, so the caller can refetch the row. */
  onResynced?: () => void;
  /** Keeps the button disabled while a resync fired from this screen is in flight. */
  syncing?: boolean;
  /** Overrides the built-in resync — for callers that need their own side effects. */
  onResync?: () => void;
  /** `start` suits a left-aligned table column. */
  align?: "start" | "end";
  /** Tighter label and resync control for dense table rows. */
  compact?: boolean;
}

export function SyncStatus({
  crmSyncStatus,
  entityType,
  entityId,
  crmType = DEFAULT_CRM_TYPE,
  onResynced,
  syncing,
  onResync,
  align = "end",
  compact = false,
}: Props) {
  const status = normalizeSyncAltoStatus(crmSyncStatus);
  const { label, className, Icon } = PRESENTATION[status];
  const [resyncing, setResyncing] = useState(false);
  // Table rows unmount on refetch, which can land before the request settles.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // crm-gateway keys the agency off the selected branch, falling back to the
  // legal entity — the store only ever holds one of the two.
  const agencyId = useSettingsStore((s) => s.branchId ?? s.legalEntityId);

  const handleResync = async () => {
    // Without an id the writeback would target nothing, so fail loudly here
    // rather than posting an empty entityId.
    if (!entityType || !entityId) return;
    if (!agencyId) {
      showActionErrorFromUnknown(null, "Select a branch before syncing.");
      return;
    }
    setResyncing(true);
    try {
      await crmGatewaySyncService.resync({ entityId, agencyId, crmType, entityType });
      toast.success("Sync queued");
      onResynced?.();
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to queue sync");
    } finally {
      if (mounted.current) setResyncing(false);
    }
  };

  // A caller-supplied handler wins over the built-in call.
  const resync = onResync ?? (entityType && entityId ? handleResync : undefined);
  // Disables the button, so an in-flight sync can't be re-fired.
  const busy = Boolean(syncing) || resyncing || isSyncAltoSyncing(status);

  return (
    // Status and action share one row, so the label's width doesn't shift the icon.
    <div
      className={cn(
        "flex items-center gap-1",
        align === "end" ? "justify-end" : "justify-start",
      )}
    >
      <div className={cn("flex items-center gap-1.5 font-medium", compact ? "text-[11px]" : "text-xs", className)}>
        <Icon size={compact ? 12 : 14} className={cn(status === "in_progress" && "animate-spin")} />
        <span>{label}</span>
      </div>
      <IconButton
        type="button"
        label={busy ? "Sync in progress" : "Sync now"}
        disabled={busy}
        className={cn(
          "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent",
          compact ? "h-7 w-7" : "h-8 w-8",
        )}
        // The icon sits inside clickable table rows, so keep the click from
        // bubbling up and opening the row's drawer.
        onClick={(e) => {
          e.stopPropagation();
          resync?.();
        }}
      >
        <RefreshCw size={compact ? 14 : 17} />
      </IconButton>
    </div>
  );
}
